import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Calendar, MapPin, Clock, ArrowRight, Loader2, Ticket } from "lucide-react";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}
function daysAway(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  const days = Math.ceil(ms / (24 * 3600 * 1000));
  if (days === 0) return "TODAY";
  if (days === 1) return "TOMORROW";
  if (days < 0) return `${Math.abs(days)}d ago`;
  return `IN ${days} DAYS`;
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="grain" data-testid="events-page">
      <section className="section-pad pt-32">
        <div className="container-vg">
          <div className="overline">Field Events · 2026</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter mt-4 max-w-4xl">
            Meet the <span className="text-yellow-400">crew.</span>
          </h1>
          <p className="text-zinc-400 mt-6 max-w-2xl text-lg leading-relaxed">
            Upcoming Vortex Ghost meet &amp; greets, chase-season kickoffs, community gear demos, and public briefings.
            Free to attend unless noted. Bring questions.
          </p>
        </div>
      </section>

      <section className="pb-32">
        <div className="container-vg">
          {loading ? (
            <div className="flex items-center gap-3 text-zinc-500 py-16">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading events…
            </div>
          ) : events.length === 0 ? (
            <div className="card-vg p-16 text-center">
              <Calendar className="w-12 h-12 text-zinc-600 mx-auto" />
              <div className="overline mt-6">No Upcoming Events</div>
              <h2 className="font-heading text-3xl font-bold mt-3">Check back soon.</h2>
              <p className="text-zinc-400 mt-3 max-w-md mx-auto">
                We schedule meet &amp; greets around chase season. Follow our socials to be first to know.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {events.map((ev, i) => (
                <EventCard key={ev.id} event={ev} highlight={i === 0} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function EventCard({ event, highlight }) {
  const start = new Date(event.starts_at);
  const day = start.toLocaleDateString([], { day: "numeric" });
  const month = start.toLocaleDateString([], { month: "short" }).toUpperCase();
  const timeStr = fmtTime(event.starts_at) + (event.ends_at ? ` – ${fmtTime(event.ends_at)}` : "");

  const backend = process.env.REACT_APP_BACKEND_URL;
  const cover = event.has_cover ? `${backend}/api/events/${event.id}/cover` : null;

  return (
    <article
      className={`card-vg overflow-hidden group ${highlight ? "border-yellow-400/60" : ""}`}
      data-testid={`event-card-${event.id}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-12">
        {/* Date column */}
        <div className={`md:col-span-2 p-6 flex md:flex-col items-center md:items-start justify-between md:justify-center gap-3 md:gap-1 ${highlight ? "bg-yellow-400/5" : "bg-white/[0.02]"}`}>
          <div className="font-heading">
            <div className={`text-4xl md:text-5xl font-black leading-none ${highlight ? "text-yellow-400" : "text-white"}`}>{day}</div>
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-400 mt-1">{month}</div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 md:mt-2">
            {daysAway(event.starts_at)}
          </div>
        </div>

        {/* Cover if present */}
        {cover && (
          <div className="md:col-span-3 relative min-h-[180px] bg-[#0d0f12]">
            <img
              src={cover}
              alt={event.title}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid={`event-cover-${event.id}`}
            />
          </div>
        )}

        {/* Body */}
        <div className={`p-6 md:p-8 flex flex-col justify-center ${cover ? "md:col-span-7" : "md:col-span-10"}`}>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`overline ${highlight ? "!text-yellow-400" : ""}`}>{event.event_type || "Event"}</span>
            {highlight && <span className="text-[10px] font-mono uppercase tracking-widest bg-yellow-400 text-black px-2 py-0.5">Next Up</span>}
          </div>

          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white group-hover:text-yellow-400 transition-colors">
            {event.title}
          </h2>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm text-zinc-400 font-mono">
            <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4 text-yellow-400" /> {fmtDate(event.starts_at)}</span>
            <span className="inline-flex items-center gap-2"><Ticket className="w-4 h-4 text-yellow-400" /> {timeStr}</span>
            <span className="inline-flex items-center gap-2"><MapPin className="w-4 h-4 text-yellow-400" /> {event.location}</span>
          </div>

          <p className="text-zinc-300 mt-4 leading-relaxed whitespace-pre-line">{event.description}</p>

          {event.rsvp_url && (
            <div className="mt-6">
              <a
                href={event.rsvp_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex"
                data-testid={`event-rsvp-${event.id}`}
              >
                RSVP <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
